import React from 'react'
import {
  Form, FormGroup, ControlLabel, FormControl,
  Row, Col, Checkbox, Panel, ButtonGroup, 
  ButtonToolbar, ToggleButtonGroup, ToggleButton
} from 'react-bootstrap'
import DocumentTitle from "react-document-title"
import { debounce } from 'lodash'

import FilesSelector from '../lib/FilesSelector.jsx'
import { PriceEditor } from '../components/Price'
import CitySelector from '../cities/CitySelector.jsx'
import MarkdownField from '../lib/MarkdownField.jsx'
import SupplierSelector from '../suppliers/SupplierSelector.jsx'
import { fetchGeolocation, getGeolocationParameters } from '../lib/AppTools'
import { getText, nameOrTitle } from '../lib/text.js'

import TagsList from "../tags/TagsList"
import TranslateLink from '../components/TranslateLink'
import Button from '../components/Button.jsx'

import TimeTableEditor from '../timetable/editor/TimeTableEditor'
import TimeTableCalendar from '../timetable/TimeTableCalendar'
import HoverTip from '../components/HoverTip'

import "./ActivityEditor.less"

/** Activity Editor
 * stateless, pure
 */

const textFields = {
  notesForAgencies: {
    key: "notesForAgencies",
    label: "🇨🇳 Nota para agencias",
    sublabel: "Nota para las agencias; ¿con cuánta antelación tienen que comprarla? ¿de qué cosas tienen que avisar al cliente?",
    translate: true
  },
  longDescription: {
    type: "md",
    key: "longDescription",
    label: "🇨🇳 Descripción completa",
    sublabel: "Descripción larga; ¿qué es el lugar? ¿cuál es su historia (resumida)?",
    translate: true
  },
  description: {
    type: "md",
    key: "description",
    label: "🇨🇳 Descripción breve",
    sublabel: "En una o dos frases, ¿qué es este sitio?",
    translate: true
  },
  clientNote: {
    type: "plain",
    key: "clientNote",
    label: "🇨🇳 Nota para el cliente",
    sublabel: "Para contrato: ¿Algo que deba saber el cliente ANTES de comprar? ¿Condiciones, qué incluye el precio...?",
    translate: true
  },
  visitInstructions: {
    type: "md",
    key: "visitInstructions",
    label: "🇨🇳 Instrucciones para la visita",
    sublabel: "¿Hay mucha cola? ¿Es importante la puntualidad? ¿Hay que hacer algo para sacar la entrada?",
    botlabel: (
      <ul style = {{ fontStyle: "italic", listStyleType: "none" }}>
        <li style = { { fontWeight: "bold", textDecoration: "underline" }}>
          {getText("Claves para sustituir en el texto:")}
        </li>
        <li>{getText("Ejemplo de entrada")}: $Entrada</li>
      </ul>
    ),
    translate: true
  },
}

type Props = {
  fixedCity: ?boolean,
  Cities: Object,
  activity: Object,
  onChange: (string, any) => void,
  citiesIdsList: string[],
}

class ActivityEditor extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { searchedAddress: null, geolocation: null, timetableEditor: true }
    this.dataForGeolocation = {}
    this.debouncedGeolocation = debounce((dataForGeolocation) => {
      this.getGeolocation(dataForGeolocation)
    }, 1000, { trailing : true })
  }

  props: Props

  componentDidUpdate(prevProps) {
    this.checkAdress(prevProps)
  }

  checkAdress(prevProps) {
    if (
      this.props.activity.address != prevProps.activity.address
      || this.props.activity.city != prevProps.activity.city
      || this.props.activity.name != prevProps.activity.name
    ) {
      this.debouncedGeolocation({
        nextAddress : this.props.activity.address,
        currentAddress : prevProps.activity.address,
        nextCity : this.props.activity.city,
        currentCity : prevProps.activity.city,
        nextName : this.props.activity.name,
        currentName : prevProps.activity.name
      })
    }
  }

  getGeolocationText() {
    const g = this.props.activity.preciseLocation
    return g ? `${g.address} (${g.longitude}, ${g.latitude})` : "..."
  }

  getGeolocation(dataForGeolocation) {
    if (!dataForGeolocation.nextAddress) {
      this.props.onChange({
        preciseLocation: {
          address: "",
          longitude: null,
          latitude: null,
        }
      })
    }
    else {
      const searchedAddress = getGeolocationParameters(dataForGeolocation)
      this.setState({ searchedAddress })
      if (searchedAddress) {
        fetchGeolocation(searchedAddress).then(geolocation => {
          if (searchedAddress == this.state.searchedAddress) {
            this.props.onChange({
              preciseLocation: {
                address: geolocation.formatted_address,
                longitude: geolocation.geometry.location.lng,
                latitude: geolocation.geometry.location.lat,
              }
            })
          }
        })
      }
    }
  }

  render() {
    const { Cities, activity, tags, onChange } = this.props

    const styles = {
      fg: { display: "block", padding: 2 },
      label: { width: 160 },
      sublabel: { color: "#555", marginBottom: 2, fontSize: 14, fontStyle: "italic" },
    }

    return (
      <div className={`activity-editor ${activity.deleted ? "deleted" : ""}`}>
        <DocumentTitle
          title={`${getText("Actividad")}: ${nameOrTitle(activity)}`}
        />

        <TagsList
          allTags={tags}
          selectedTags={activity.tags}
          onChange={tags => onChange({ tags })}
          onClick={tagId => console.log(`nothing to do with clicked tag ${tagId}`)}
          tagType="activities"
        />

        <Form inline>
          <FormGroup style={styles.fg}>
            <ControlLabel style={styles.label}>{getText("spanish_title")}: </ControlLabel>
            <FormControl
              className= "esname-control"
              type="text"
              value={activity.name}
              onChange={evt => onChange({ name: evt.target.value })}
            />
          </FormGroup>
          <FormGroup style={styles.fg}>
            <ControlLabel style={styles.label}>{getText("chinese_title")}: </ControlLabel>
            <FormControl
              className= "chname-control"
              type="text"
              value={activity.title}
              onChange={evt => onChange({ title: evt.target.value })}
            />
            <TranslateLink text={activity.title} />
          </FormGroup>
          <FormGroup style={styles.fg}>
            <ControlLabel style={styles.label}>{getText("english_title")}: </ControlLabel>
            <FormControl
              className= "enname-control"
              type="text"
              value={activity.englishName}
              onChange={evt => onChange({ englishName: evt.target.value })}
            />
          </FormGroup>
        </Form>

        <CitySelector
          style={{ marginTop: 15, marginBottom: 15 }}
          cityId={activity.city || ""}
          onChange={v => onChange({ city: v })}
          labelWidth={styles.label.width}
          inlineForm
          countryLabel="País:"
          cityLabel="Ciudad:"
        />

        <SupplierSelector
          supplierId={activity.supplier || null}
          onChange={v => onChange({ supplier: v })}
          inline
          labelWidth={styles.label.width}
        />

        <Form inline>

          <FormGroup style={styles.fg}>
            <ControlLabel style={styles.label}>{getText("Duración (horas)")}:</ControlLabel>
            <FormControl
              type="text"
              name="duration"
              style={{ maxWidth: 206 }}
              value={activity.duration || 0}
              onChange={evt => onChange({
                duration: Number(evt.target.value) || Number.parseFloat(evt.target.value)
              })}
            />
          </FormGroup>

          <FormGroup style={styles.fg}>
            <ControlLabel style={styles.label}>{getText("Teléfono")}:</ControlLabel>
            <FormControl
              type="text"
              value={activity.phoneNumber}
              onChange={evt => onChange({ phoneNumber: evt.target.value })}
            />
          </FormGroup>

        </Form>

        <FormGroup style={{ marginTop: 20 }}>
          <ControlLabel>{getText("Dirección (completa)")}:</ControlLabel>
          <FormControl
            type="text"
            value={activity.address}
            onChange={evt => onChange({ address: evt.target.value })}
          />
          <span>Geolocation: {this.getGeolocationText()}</span>
        </FormGroup>

        <div key="notes" style={{ paddingTop: 2, marginBottom: 10 }}>
          <MarkdownField
            onChange={x => onChange({ notes: x })}
            value={activity.notes}
            label={
              <span>
                {getText("🇪🇸 Nota interna")}
              </span>
            }
            sublabel={"Nota para nosotros, que nunca va a ver el cliente; ¿cuándo no se debe reservar?"}
            style={{ marginBottom: 5 }}
          />
        </div>

        <hr />

        <ButtonToolbar style={{ marginBottom: 10 }}>
          <ToggleButtonGroup type="radio" name="options"
            value={
              (activity.unknownAvailability && 1)
              || (activity.alwaysOpen && 2)
              || (activity.timetable.allDay && 4)
              || 3
            }
            onChange={v => {
              if (v == 1) onChange({ alwaysOpen: false, unknownAvailability: true })
              else if (v == 2) onChange({ alwaysOpen: true, unknownAvailability: false })
              else if (v == 3) onChange({ alwaysOpen: false, unknownAvailability: false, timetable: { ...activity.timetable, allDay: false }})
              else if (v == 4) onChange({ alwaysOpen: false, unknownAvailability: false, timetable: { ...activity.timetable, allDay: true }})
              else alert("Error WTF-101291")
            }}
          >
            {
              [
                {
                  v: 1, 
                  text: "❓ Disponibilidad desconocida" , 
                  help: "No tenemos ni idea de cuándo está disponible, ni fechas ni horarios" 
                },
                {
                  v: 2, 
                  text: "🎉 Disponible siempre", 
                  help: "Siempre está abierto (sitios públicos, como plazas o monumentos)"
                },
                {
                  v: 3, 
                  text: "🎟️ Horario con turnos",
                  help: "Se debe elegir uno entre varios turnos al día" 
                },
                { 
                  v: 4, 
                  text: "🕓 Horario \"libre\"", 
                  help: "Se puede ir en cualquier momento dentro de un rango cada día" 
                },
              ].map(({ v, text, help }) => (
                <ToggleButton key={v} value={v}>
                  <HoverTip key={v} text={help}>
                    <span>{text}</span>
                  </HoverTip>
                </ToggleButton>
              ))
            }
          </ToggleButtonGroup>
        </ButtonToolbar>

        {
          !activity.unknownAvailability && !activity.alwaysOpen && ( 
            <Panel className="timetable-panel bordered-panel" bsStyle="info">
              <Panel.Heading>
                <Panel.Title>📅 Disponibilidad</Panel.Title>
                <ButtonGroup>
                  <Button
                    glyph="calendar" label="Ver calendario"
                    active={!this.state.timetableEditor}
                    onClick={() => this.setState({ timetableEditor: false })}
                  />
                  <Button
                    glyph="pencil" label="Editar"
                    active={this.state.timetableEditor}
                    onClick={() => this.setState({ timetableEditor: true })}
                  />
                </ButtonGroup>
              </Panel.Heading>
              <Panel.Body>
                {
                  this.state.timetableEditor
                    ? (
                      <TimeTableEditor
                        includeTypeSwitch={false}
                        timetable={activity.timetable}
                        onChange={timetable => onChange({ timetable })}
                      />
                    )
                    : (<TimeTableCalendar timetable={activity.timetable} />)
                }
              </Panel.Body>
            </Panel>
          )
        }
        
        <hr />
          
        {
          [ "notesForAgencies", "longDescription", "description", "clientNote", "visitInstructions"]
            .map(key => textFields[key])
            .map(({ label, sublabel, botlabel, key, type, translate }) => (
              <div key={key} style={{ paddingTop: 2, marginBottom: 10 }}>
                {
                  type == "plain"
                    ? (
                      <div>
                        <ControlLabel>{getText(label)}{translate && <TranslateLink text={activity[key]} />}</ControlLabel>
                        <div style={styles.sublabel}>{sublabel}</div>
                        <FormControl value={activity[key]} onChange={evt => onChange({ [key]: evt.target.value })} />
                      </div>
                    )
                    : (
                      <MarkdownField
                        onChange={x => onChange({ [key]: x })}
                        value={activity[key]}
                        label={
                          <span>
                            {getText(label)}
                            {translate && <small> <TranslateLink text={activity[key]} /></small>}
                          </span>
                        }
                        sublabel={sublabel}
                        botLabel={botlabel}
                        style={{ marginBottom: 5 }}
                      />
                    )
                }
              </div>
            ))
        }

        <hr />

        <Row>
          <Col md={6}>
            <strong>{getText("Precio")}</strong>
            <PriceEditor
              price={activity.price}
              onChange={price => onChange({ price })}
            />
          </Col>
          <Col md={6}>
            <strong>{getText("Coste")}</strong>
            <PriceEditor
              price={activity.cost}
              onChange={cost => onChange({ cost })}
            />
          </Col>
        </Row>

        <FilesSelector
          title="Ficheros"
          includeNotes
          value={activity.files}
          fileNotes={activity.fileNotes}
          onChange={obj => onChange({ files: obj })}
          onNotesChange={obj => onChange({ fileNotes: obj })}
          labels={{
            doc1Page: getText("Página(s) para guión #1"),
            activityImg: getText("Imagen para App (y página agencias)"),
            ticketExample: getText("Ejemplo de entrada"),
          }}
          extensions={{
            doc1Page: ["pdf"],
            activityImg: ["jpeg", "jpg", "png"],
            ticketExample: ["jpeg", "jpg", "png"],
          }}
        />

        {
          !activity.files.activityImg && (
            <a
              href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${Cities[activity.city].name} ${activity.name}`)}&tbs=sur:fc`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getText("Busca imágenes en Google")}
            </a>
          )
        }

        <hr />

        <Checkbox
          style={{ marginBottom: 10 }}
          checked={activity.hasVoucher}
          onChange={evt => onChange({ hasVoucher: evt.target.checked })}
        >
          <span style={{ fontWeight: "bold" }}>{getText("Voucher")}</span>
        </Checkbox>

        <MarkdownField
          onChange={x => onChange({ voucherDescription: x })}
          value={activity.voucherDescription}
          label={(
            <span>
              {getText("voucher_description")}
              <small><TranslateLink text={activity.voucherDescription} /></small>
            </span>
          )}
          disabled = {!activity.hasVoucher}
          botLabel = {(
            <ul style={{ fontStyle: "italic", listStyleType: "none" }}>
              <li style={{ fontWeight: "bold", textDecoration: "underline" }}>
                {getText("Claves para sustituir en el texto:")}
              </li>
              <li>{getText("Nombre")}: $Nombre</li>
              <li>{getText("Actividad")}: $Actividad</li>
              <li>{getText("Fecha")}: $Fecha</li>
              <li>{getText("Hora")}: $Hora</li>
              <li>{getText("Dirección")}: $Direccion</li>
              <li>{getText("Teléfono")}: $Telefono</li>
            </ul>
          )}
        />

        <FilesSelector
          title={getText("Ficheros Voucher")}
          includeNotes
          value={activity.voucherFiles}
          fileNotes={activity.voucherFileNotes}
          onChange={obj => onChange({ voucherFiles: obj })}
          onNotesChange={obj => onChange({ voucherFileNotes: obj })}
          labels={{
            image1: getText("Imágen #1"),
            image2: getText("Imágen #2")
          }}
          extensions={{ image1: ["jpg", "jpeg", "png"], image2: ["jpg", "jpeg", "png"] }}
          disabled={!activity.hasVoucher}
        /> 

      </div>
    )
  }
}

ActivityEditor.defaultProps = {
  fixedCity: false,
  citiesIdsList: []
}

export default ActivityEditor
